'use client';

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { analytics } from '@/lib/analytics/registry';

type NsfwMode = 'mask' | 'filter' | 'allow';

export interface ConnectedAccountInfo {
  id: string;
  provider: string;
  authType: string;
  providerUserId: string | null;
  providerUsername: string | null;
  scopes: string[] | null;
  needsReauth: boolean | null;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  email: string | null;
  username: string;
  /** The editable label. There is NO `name` field on the session user: the
   *  users table only has `username` + `display_name`, and /api/auth/session
   *  returns the row as-is. Avatars seed on `username` (see lib/avatar.ts). */
  displayName: string;
  avatarUrl: string | null;
  // Runtime can be 'guest' for ephemeral guest sessions; kept narrow here
  // because consumers (avatar viewers) only branch human-vs-agent, and a guest
  // renders as a human. The user MENU reads accountType via its own
  // `UserMenuUser` prop (widened to include 'guest').
  accountType: 'human' | 'agent';
  role: 'user' | 'moderator' | 'admin';
  reputationScore: string;
  referralCode: string | null;
  bio: string | null;
  preferences?: { nsfwMode?: NsfwMode; [key: string]: any } | null;
  /** ISO date of the ToS version last accepted; read by TosUpdateNotice (G2). */
  acceptedTermsVersion?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  nsfwMode: NsfwMode;
  connectedAccounts: ConnectedAccountInfo[];
  availableProviders: string[];
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

/** True when two session values carry the same data (small objects; the order of keys is the server's own). */
function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  nsfwMode: 'mask',
  connectedAccounts: [],
  availableProviders: [],
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccountInfo[]>([]);
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);

  const refresh = async () => {
    try {
      const res = await fetch('/api/auth/session');
      if (!res.ok) return;
      const data = await res.json();
      const u = data.user ?? null;
      // Keep the SAME object when nothing changed. This runs on every
      // return to the tab, and a new identity for an unchanged user re-ran
      // every effect that depends on it (the app bridge's message handler
      // among them: the e2e run of 2026-09-30, bug 1).
      setUser((prev) => (sameJson(prev, u) ? prev : u));
      setConnectedAccounts((prev) => (sameJson(prev, data.connectedAccounts ?? []) ? prev : data.connectedAccounts ?? []));
      setAvailableProviders((prev) => (sameJson(prev, data.availableProviders ?? []) ? prev : data.availableProviders ?? []));
      if (u) {
        analytics.identify(u.id, 'essential', {
          username: u.username,
          accountType: u.accountType,
          role: u.role,
        });
        try {
          window.localStorage.setItem('robutler_has_logged_in', '1');
        } catch {
          // ignore storage errors (private mode, quota, etc.)
        }
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      analytics.reset('essential');
      await fetch('/api/auth/logout', { method: 'POST' });
      setUser(null);
      setConnectedAccounts([]);
      setAvailableProviders([]);
      window.location.href = '/';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const nsfwMode: NsfwMode = (user?.preferences?.nsfwMode as NsfwMode) || 'mask';
  const didMount = useRef(false);

  useEffect(() => {
    refresh();
    didMount.current = true;

    // Re-fetch session when page is restored from bfcache (back/forward navigation
    // can skip re-mounting the component, leaving stale auth state).
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) refresh();
    };
    window.addEventListener('pageshow', onPageShow);

    // Session REVALIDATION for long-open tabs. The session cookie is a 7-day
    // JWT; before this, auth state was fetched exactly once at mount, so a
    // tab older than its cookie kept LOOKING signed in while every API call
    // quietly failed as anonymous (app pages are anon-first by design, so
    // nothing server-side redirects either — observed 2026-08-05 as a
    // 4-day-stale tab hammering agent endpoints with an expired token).
    // Re-check whenever the tab regains focus, plus a slow interval while
    // visible; an expired session flips `user` to null and the signed-out
    // chrome (sign-in CTAs) takes over. No forced navigation: these surfaces
    // legitimately serve anonymous viewers.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const revalidate = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 10 * 60_000);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(revalidate);
    };
  }, []);

  // Defensive: if initial fetch resolved with no user but the logged_in cookie
  // is present (e.g. network hiccup, race with cookie propagation), retry once.
  useEffect(() => {
    if (!didMount.current || loading || user) return;
    if (document.cookie.includes('logged_in=')) {
      const t = setTimeout(refresh, 500);
      return () => clearTimeout(t);
    }
  }, [loading, user]);

  return (
    <AuthContext.Provider value={{ user, loading, nsfwMode, connectedAccounts, availableProviders, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
